import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLocale } from "@/i18n/LocaleProvider";
import { useAuth } from "@/auth/AuthProvider";
import { goToLoginAndReturn } from "@/auth/returnTo";
import { BrandMark } from "@/brand/BrandMark";
import { UserMenu } from "@/components/UserMenu";
import { useMarket } from "./MarketProvider";
import { LIST_PROPERTY_PATH, useMarketLink } from "./links";
import { MarketSwitcher } from "./MarketSwitcher";
import { useIsMobile, useV3 } from "./useV3";
import { Close, Menu } from "./Icons";

/* The site header, from home-*-1440.html and home-*-390.html.
 *
 * DESKTOP and MOBILE are different elements, not one element at two widths:
 * desktop is logo · nav · (market, locale, login, list) and mobile is
 * hamburger · logo · market · list. The design ships both; this renders
 * whichever the viewport is, rather than both with one hidden.
 *
 * NAV LABELS AND VISIBILITY ARE DATA. `nav.items` is a site_settings row, so
 * an admin can rename or hide an item without a deploy. What each item POINTS
 * AT is code -- a route is not content, and an editable destination is an
 * editable way to send visitors somewhere that does not exist.
 *
 * PROJECTS is hidden by default and stays hidden: there is no projects
 * backend. The admin toggle for it is disabled and the worker refuses to set
 * the flag, so the item cannot be switched on to lead nowhere.
 */

type NavLink = { key: string; label: string; to: string; enabled: boolean };

function useNavLinks(): NavLink[] {
  const { s, locale } = useV3();
  const { settings, market } = useMarket();
  const { profile } = useAuth();
  const link = useMarketLink();

  const label: Record<string, string> = {
    buy: s.nav.buy, rent: s.nav.rent, projects: s.nav.projects,
    advisor: s.nav.advisor, plans: s.nav.plans,
  };
  const to: Record<string, string> = {
    buy: link("/search?for=Sale"),
    rent: link("/search?for=Rent"),
    projects: link("/projects"),
    advisor: profile ? link("/account#ai") : link("/login"),
    plans: link("/pricing"),
  };

  return settings.nav.items
    .map((item) => ({
      key: item.key,
      /* An admin-set label wins over ours. `nav.labels` is a settings row, so
         renaming «الباقات» to «الأسعار» is a console edit, not a deploy. */
      label: settings.nav.labels?.[item.key]?.[locale] || label[item.key] || item.key,
      to: to[item.key] ?? link("/"),
      // The advisor is a market feature: Saudi's row can turn it off, and a
      // nav item for a feature this market does not have is a door onto a
      // 404 wearing a working link's clothes.
      enabled: item.visible
        && (item.key !== "projects")
        && (item.key !== "advisor" || market?.features.aiAdvisor !== false),
    }))
    .filter((i) => i.enabled);
}

export function HeaderV3() {
  const mobile = useIsMobile();
  return mobile ? <MobileHeader /> : <DesktopHeader />;
}

function LocaleLink() {
  const { s } = useV3();
  const { setLocale } = useLocale();
  const other = s.header.otherLocaleLang as "ar" | "en";
  /* A button, not an <a>: the design draws a link and the act is a state
     change with no URL of its own. Styled as the design's header link so it
     reads the same; made a button so it behaves the same as it looks. */
  return (
    <button type="button" className="sq-header__link" lang={other}
      onClick={() => setLocale(other)}>
      {s.header.otherLocale}
    </button>
  );
}

/** The list-your-property CTA. Signed out it stores the return path first. */
function ListCta({ className }: { className: string }) {
  const { s, locale } = useV3();
  const { profile } = useAuth();
  const { settings } = useMarket();
  const navigate = useNavigate();
  const link = useMarketLink();
  const target = link(LIST_PROPERTY_PATH);
  /* An admin-set label wins, exactly as the nav items' do. Where it POINTS is
     still code: a route is not content. */
  const label = settings.nav.cta?.label?.[locale] || s.header.listProperty;
  if (profile) return <Link className={className} to={target}>{label}</Link>;
  /* NOT a ?next= query -- Login reads a STORED return path and ignores the
     query. A link that claims to bring you back and does not is the fake
     affordance this project keeps finding. */
  return (
    <button type="button" className={className}
      onClick={() => goToLoginAndReturn(navigate, target)}>
      {label}
    </button>
  );
}

function DesktopHeader() {
  const { s } = useV3();
  const { profile } = useAuth();
  const link = useMarketLink();
  const nav = useNavLinks();

  return (
    <header data-component="header" className="sq-header">
      <Link to={link("/")} className="sq-logo" aria-label="SouqAkar">
        <BrandMark width={89} title="SouqAkar" />
      </Link>
      <nav className="sq-header__nav" aria-label={s.header.main}>
        {nav.map((i) => <Link key={i.key} to={i.to}>{i.label}</Link>)}
      </nav>
      <div className="sq-header__actions">
        <MarketSwitcher />
        <LocaleLink />
        {profile
          ? <UserMenu />
          : <Link to={link("/login")} className="sq-header__link">{s.header.login}</Link>}
        <ListCta className="sq-btn sq-btn--primary" />
      </div>
    </header>
  );
}

function MobileHeader() {
  const { s } = useV3();
  const link = useMarketLink();
  const [open, setOpen] = useState(false);

  return (
    <>
      <header data-component="header" className="sq-header">
        <button type="button" className="sq-btn sq-btn--ghost sq-btn--icon"
          aria-label={s.header.menu} aria-expanded={open} onClick={() => setOpen(true)}>
          <Menu />
        </button>
        <Link to={link("/")} className="sq-logo sq-logo--mobile" aria-label="SouqAkar">
          <BrandMark width={69} title="SouqAkar" />
        </Link>
        <span style={{ marginInlineStart: "auto" }}><MarketSwitcher compact /></span>
        <ListCta className="sq-btn sq-btn--primary sq-btn--sm" />
      </header>
      {open && <MobileDrawer onClose={() => setOpen(false)} />}
    </>
  );
}

/* The drawer the hamburger opens.
 *
 * DECLARED MISSING from the design package (docs/REBUILD-1-INVENTORY.md): the
 * 390 header draws the button and no file draws what it opens. Built from the
 * design's own sheet vocabulary -- white surface, its radius, its backdrop --
 * rather than invented in a different language. Logged in DEVIATIONS.md.
 */
function MobileDrawer({ onClose }: { onClose: () => void }) {
  const { s } = useV3();
  const { profile } = useAuth();
  const link = useMarketLink();
  const nav = useNavLinks();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div className="sq-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="sq-drawer" role="dialog" aria-modal="true" aria-label={s.header.menu}>
        <div className="sq-drawer__head">
          <BrandMark width={69} title="SouqAkar" />
          <button type="button" className="sq-btn sq-btn--ghost sq-btn--icon"
            aria-label={s.marketSuggestion.close} onClick={onClose}>
            <Close />
          </button>
        </div>
        <nav className="sq-drawer__links" aria-label={s.header.main}>
          {nav.map((i) => <Link key={i.key} to={i.to} onClick={onClose}>{i.label}</Link>)}
          {!profile && <Link to={link("/login")} onClick={onClose}>{s.header.login}</Link>}
        </nav>
        <LocaleLink />
      </div>
    </>
  );
}
